<template>
  <section id="car-aromas" class="car-aromas">
    <div class="car-aromas__art">
      <span class="art-mark">CHER</span>

      <div class="vent-diffuser" role="img" aria-label="CHER car diffuser clipped to an air vent">
        <span class="vent-diffuser__clip"><i></i><i></i></span>
        <span class="vent-diffuser__cap"></span>
        <span class="vent-diffuser__collar"></span>
        <span class="vent-diffuser__flask"><b>CHER</b></span>
      </div>

      <p class="art-caption">Made for<br>the journey.</p>
    </div>

    <div class="car-aromas__content">
      <p class="section-label">Car aromas</p>
      <h2>Take your signature<br>scent everywhere.</h2>
      <p class="description">A wooden-cap diffuser that clips onto your air vent, filled with the same fragrances you wear.</p>

      <label for="car-fragrance">Choose a fragrance</label>
      <select id="car-fragrance" v-model="selectedId">
        <option value="" disabled>Select your scent</option>
        <option v-for="item in items" :key="item.id" :value="item.id">
          {{ item.name }} — {{ formatPrice(item.basePrice) }}
        </option>
      </select>

      <div v-if="selected" class="selection">
        <span>{{ selected.notes }}</span>
        <strong>{{ formatPrice(selected.basePrice) }}</strong>
      </div>
      <button type="button" :disabled="!selected" @click="addToCart">
        Add car aroma to bag
      </button>
    </div>
  </section>
</template>

<script>
import { formatPrice } from '../data/products'

export default {
  name: 'CarAromas',
  props: {
    items: { type: Array, required: true }
  },
  data: () => ({ selectedId: '' }),
  computed: {
    selected() {
      return this.items.find(item => item.id === this.selectedId)
    }
  },
  methods: {
    formatPrice,
    addToCart() {
      this.$emit('add-to-cart', {
        cartKey: this.selected.id,
        productId: this.selected.id,
        name: `${this.selected.name} Car Aroma`,
        variantLabel: 'Car diffuser',
        price: this.selected.basePrice
      })
      this.selectedId = ''
    }
  }
}
</script>

<style scoped>
.car-aromas { display: grid; grid-template-columns: 1fr 1fr; background: #27231e; }

/* Dark dashboard interior, lit only by the diffuser. */
.car-aromas__art {
  min-height: 620px;
  position: relative;
  display: grid;
  place-items: center;
  overflow: hidden;
  background: radial-gradient(120% 95% at 74% 32%, #26333f 0%, #17212b 46%, #0c1219 100%);
}
.car-aromas__art::before {
  content: '';
  width: 430px;
  height: 430px;
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 191, 122, .32), rgba(255, 168, 88, 0) 66%);
  animation: glow 5s ease-in-out infinite;
}
.car-aromas__art::after {
  content: '';
  width: 26%;
  height: 100%;
  position: absolute;
  top: 0;
  right: 0;
  background: repeating-linear-gradient(180deg, rgba(255, 255, 255, .05) 0 9px, rgba(0, 0, 0, .3) 9px 21px);
  opacity: .5;
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 55%);
  mask-image: linear-gradient(90deg, transparent, #000 55%);
}
.art-mark {
  position: absolute;
  z-index: 3;
  top: 30px;
  left: 35px;
  color: rgba(255, 255, 255, .62);
  font-family: mulegh, Georgia, serif;
  font-size: 26px;
  letter-spacing: 5px;
}
.art-caption {
  position: absolute;
  z-index: 3;
  left: 9%;
  bottom: 7%;
  color: #fff;
  font-family: Georgia, serif;
  font-size: 37px;
  line-height: 1.05;
  text-shadow: 0 3px 18px rgba(0, 0, 0, .6);
}

.vent-diffuser {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding-top: 52px;
  transform-origin: 50% 6%;
  filter: drop-shadow(0 26px 26px rgba(0, 0, 0, .55));
  animation: sway 6.5s ease-in-out infinite;
}

/* Two ribbed plastic balls on steel stems that grip the vent fin. */
.vent-diffuser__clip {
  width: 96px;
  height: 58px;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-44%);
}
.vent-diffuser__clip i {
  width: 4px;
  height: 44px;
  position: absolute;
  bottom: 0;
  border-radius: 2px;
  background: linear-gradient(90deg, #2b2f33, #70767b 45%, #383c40);
  transform-origin: bottom center;
}
.vent-diffuser__clip i:first-child { left: 28px; transform: rotate(-17deg); }
.vent-diffuser__clip i:last-child { left: 54px; transform: rotate(13deg); }
.vent-diffuser__clip i::before {
  content: '';
  width: 27px;
  height: 27px;
  position: absolute;
  top: -20px;
  left: 50%;
  margin-left: -13.5px;
  border-radius: 50%;
  background:
    repeating-linear-gradient(180deg, rgba(0, 0, 0, .17) 0 2px, rgba(255, 255, 255, 0) 2px 6px),
    radial-gradient(circle at 34% 28%, #d3d7da 0%, #9ba1a5 56%, #6b7175 100%);
  box-shadow: inset -2px -3px 6px rgba(0, 0, 0, .28);
}
.vent-diffuser__clip i:last-child::before {
  width: 23px;
  height: 23px;
  margin-left: -11.5px;
  top: -17px;
}

/* Walnut cap, wider than the flask, with a lit top face. */
.vent-diffuser__cap {
  width: 86px;
  height: 60px;
  position: relative;
  border-radius: 3px;
  background:
    repeating-linear-gradient(87deg, rgba(0, 0, 0, .15) 0 3px, rgba(255, 255, 255, .05) 3px 8px),
    linear-gradient(100deg, #35271e 0%, #5b4432 36%, #48351f 63%, #2b211a 100%);
  box-shadow:
    inset 0 -7px 11px rgba(0, 0, 0, .42),
    inset 0 2px 0 rgba(255, 255, 255, .07);
}
.vent-diffuser__cap::before {
  content: '';
  height: 17px;
  position: absolute;
  top: -13px;
  right: 9px;
  left: 3px;
  border-radius: 2px;
  background: linear-gradient(180deg, #8d6a4b 0%, #6d5039 45%, #43311f 100%);
  clip-path: polygon(12% 0, 100% 14%, 93% 100%, 0 100%);
}

/* Warm brass ring where the cap meets the glass. */
.vent-diffuser__collar {
  width: 76px;
  height: 11px;
  margin-top: -4px;
  border-radius: 2px;
  background: linear-gradient(90deg, #b08a58, #f3dcb1 38%, #d9b585 68%, #9a7749);
  box-shadow: 0 0 18px rgba(255, 205, 140, .6);
}

/* Frosted flask, glowing from the inside. */
.vent-diffuser__flask {
  width: 110px;
  height: 118px;
  position: relative;
  margin-top: -3px;
  display: grid;
  place-items: center;
  border-radius: 7px 7px 10px 10px;
  background: linear-gradient(180deg, rgba(255, 251, 243, .96) 0%, rgba(255, 240, 216, .92) 46%, rgba(247, 226, 193, .88) 100%);
  box-shadow:
    inset 0 0 24px rgba(255, 255, 255, .9),
    inset 0 -12px 20px rgba(212, 173, 120, .45),
    0 0 60px rgba(255, 201, 132, .55),
    0 0 120px rgba(255, 180, 98, .32);
}
.vent-diffuser__flask::before {
  content: '';
  width: 27px;
  height: 72%;
  position: absolute;
  top: 9px;
  left: 11px;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255, 255, 255, .95), rgba(255, 255, 255, .2));
  filter: blur(5px);
}
.vent-diffuser__flask b {
  z-index: 1;
  color: #2b241c;
  font-family: mulegh, Georgia, serif;
  font-size: 28px;
  font-weight: 400;
  line-height: 1;
  letter-spacing: 2px;
  opacity: .92;
}

@keyframes sway {
  0%, 100% { transform: rotate(-2.2deg); }
  50% { transform: rotate(2.2deg); }
}
@keyframes glow {
  0%, 100% { opacity: .78; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
}
@media (prefers-reduced-motion: reduce) {
  .vent-diffuser, .car-aromas__art::before { animation: none; }
}

.car-aromas__content { display: flex; justify-content: center; flex-direction: column; padding: 70px max(30px, 8vw); color: #fff; }
.section-label { color: #c59370; font-size: 11px; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; }
h2 { margin: 10px 0 25px; font-family: Georgia, serif; font-size: clamp(38px, 4vw, 57px); font-weight: 400; line-height: 1.06; }
.description { max-width: 480px; margin: 0 0 37px; color: #aaa198; font-size: 14px; line-height: 1.7; }
label { margin-bottom: 8px; color: #bcb2a8; font-size: 10px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; }
select { width: 100%; height: 52px; padding: 0 13px; border: 1px solid #5d554d; border-radius: 8px; outline: 0; color: #eee8e2; background: #322e28; }
.selection { display: flex; justify-content: space-between; gap: 20px; margin-top: 13px; color: #aaa198; font-size: 12px; }
.selection strong { color: #fff; }
.car-aromas__content button { margin-top: 28px; padding: 15px; border: 0; border-radius: 8px; color: #2c271f; background: #e8ded2; font-weight: 800; }
.car-aromas__content button:disabled { opacity: .35; }
@media (max-width: 800px) {
  .car-aromas { grid-template-columns: 1fr; }
  .car-aromas__art { min-height: 530px; }
  .car-aromas__content { padding: 70px 25px; }
  .art-caption { font-size: 30px; }
}
</style>
